import React, { useState, useEffect, useRef } from 'react'
import { Formik, Form } from 'formik'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import * as Yup from 'yup'
import Bugsnag from '@bugsnag/js'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import { useParams } from 'react-router-dom'
import EditPersonalTab from '../editTabs/EditPersonalTab'
import EditBankTab from '../editTabs/EditBankTab'
import EditTaxTab from '../editTabs/EditTaxTab'
import EditEmploymentTab from '../editTabs/EditEmploymentTabs'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { handleValidationErrors } from 'services/helper/handleValidationErrors'
import { _convertAPIDate, _ICBirthdateValidation } from 'services/helper/DateTime'

// API
import {
  // GET
  getPersonalData,
  getEmploymentData,
  getBankData,
  getTaxData,
  // POST
  postAllData,
} from 'services/api/modules/staff-management/staffManagementDashboard'
import { salaryCategories, banks, countries, profileDepartments } from 'services/api/dropdown'
import { format } from 'date-fns'
import { getCompanyDetails } from 'services/api/company-details'
import AdxButton from 'components/custom/AdxButton'

const EPFCategory = [
  {
    id: 1,
    category: 'Category 1',
    description:
      'Malaysian/Permanent Residents/Non-Malaysians (registered as member before 01/08/1998) AND age below 60 years old',
  },
  {
    id: 2,
    category: 'Category 2',
    description:
      'Non-Malaysians (registered as member before 01/08/1998) AND age below 60 years old',
  },
  {
    id: 3,
    category: 'Category 3',
    description:
      'Permanent Residents/Non-Malaysians (registered as member before 01/08/1998) AND age below 60 years old',
  },
  {
    id: 4,
    category: 'Category 4',
    description:
      'Non-Malaysians (registered as member before 01/08/1998) AND age 60 years old and above',
  },
  {
    id: 5,
    category: 'Category 5',
    description: 'Malaysians AND age 60 years old and above',
  },
]

const SOCSOCategoryLocal = [
  {
    id: 1,
    category: 'Category 1',
    description:
      'For employees who are less than 60 years of age, except for those who have attained 55 years of age and have no prior contributions before they reach 55',
  },
  {
    id: 2,
    category: 'Category 2',
    description:
      'For employees who have reached the age of 60 OR for eligible new employees who are 55 years of age',
  },
]

const SOCSOCategoryForeign = [
  {
    id: 1,
    category: 'Category 1 (Injury + Invalidity)',
    description:
      'For foreign workers who first enter or contribute for the first time under PERKESO Invalidity Scheme under the age of 55',
  },
  {
    id: 2,
    category: 'Category 2 (Injury only)',
    description:
      'For foreign workers who have reached 55 years old or older at first entry to the scheme OR who have reached 60 years old and are still working',
  },
]

const SOCSOWorkerTypeOptions = [
  { id: 1, label: 'Local employee' },
  { id: 2, label: 'Foreign worker' },
]


const EditAllStaff = () => {
  const queryClient = useQueryClient()
  const formikRef = useRef()
  const dobIDValidateText = 'Date of Birth in ID number must be matched'

  // const legalAge = new Date().getFullYear() - 18;
  // const selectBirthDate = new Date(legalAge, new Date().getMonth(), new Date().getDate());

  const navigate = useNavigate()
  const params = useParams()
  const [pageState, setPageState] = useState('')
  const [formikData, setFormikData] = useState('')
  const [errorTabs, setErrorTabs] = useState([])

  const [initialValues, setInitialValues] = useState({
    //basic details
    name: '',
    gender: '',
    nationality: '',
    disability_status: '',
    dob: '',
    id_type: '',
    id_number: '',

    //contact details
    phone_country_id: '',
    phone_no: '',
    personal_email: '',
    address: '',
    postcode: '',
    state: '',
    country: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone_country_id: '',
    emergency_contact_phone_no: '',

    //employment details
    employment_type: '',
    hiring_date: '',
    commencement_date: '',

    //job information
    employee_id: '',
    designation: '',
    job_description: '',
    department_id: '',
    reporting_to_id: {},

    //salary details
    salary_category: '',
    basic_salary: '',
    bank_id: '',
    bank_acc_number: '',

    //statutory details
    epf_status: '',
    epf_category: '',
    epf_acc_number: '',
    epf_employee_contribution_rate: '',
    employee_custom_contribution_rate: '',
    epf_employer_contribution_rate: '',
    socso_worker_type: 1,
    socso_status: '',
    socso_category: '',
    socso_acc_number: '',
    eis_status: '',
    hrdf_status: '',
    zakat_status: '',
    zakat_acc_number: '',
    zakat_amount: '',

    //tax details
    resident_status: '',
    income_tax_number: '',
    previous_mtd: '',
    epf_contribution: '',
    gross_remuneration: '',

    //household details
    marriage_status: '',
    spouse_disability_status: '',
    children: [],
  })

  let tabNames = ['Personal', 'Employment', 'Bank & Statutory', 'Tax']

  let sortedObjectAll = [
    [
      //basic details
      'name',
      'gender',
      'nationality',
      'disability_status',
      'dob',
      'id_type',
      'id_number',

      //contact details
      'phone_country_id',
      'phone_no',
      'personal_email',
      'address',
      'postcode',
      'state',
      'country',
      'emergency_contact_name',
      'emergency_contact_relationship',
      'emergency_contact_phone_country_id',
      'emergency_contact_phone_no',
    ],
    [
      //employment details
      'employment_type',
      'hiring_date',
      'commencement_date',

      //job information
      'employee_id',
      'designation',
      'job_description',
      'department_id',
      'reporting_to_id',
    ],
    [
      //salary details
      'salary_category',
      'basic_salary',
      'bank_id',
      'bank_acc_number',

      //statutory details
      'epf_status',
      'epf_category',
      'epf_acc_number',
      'epf_employee_contribution_rate',
      'employee_custom_contribution_rate',
      'epf_employer_contribution_rate',
      'socso_worker_type',
      'socso_status',
      'socso_category',
      'socso_acc_number',
      'eis_status',
      'zakat_status',
      'zakat_acc_number',
      'zakat_amount',
    ],
    [
      //tax details
      'resident_status',
      'income_tax_number',
      'previous_mtd',
      'epf_contribution',
      'gross_remuneration',

      //household details
      'marriage_status',
      'spouse_disability_status',
      'children',
    ],
  ]

  // Personal Tab
  const {
    // isLoading,
    // error,
    data: personal,
  } = useQuery(
    ['employeePersonalData', params.id],
    async () => {
      const { data } = await getPersonalData(params.id)
      return data
    },
    {
      onError: (err) => {
        Bugsnag.notify(err)
      },
    }
  )

  const { data: countriesList } = useQuery(
    'countriesList',
    async () => {
      const { data } = await countries()
      return data
    },
    {
      onError: (err) => {
        Bugsnag.notify(err)
      },
    }
  )

  // Employment Tab
  const {
    // isLoading,
    // error,
    data: employment,
  } = useQuery(
    ['employeeEmploymentData', params.id],
    async () => {
      const { data } = await getEmploymentData(params.id)
      return data
    },
    {
      onError: (err) => {
        Bugsnag.notify(err)
      },
    }
  )

  const { data: departmentList } = useQuery(
    'employeeProfileDepartmentList',
    async () => {
      const { data } = await profileDepartments()
      return data
    },
    {
      onError: (err) => {
        Bugsnag.notify(err)
      },
    }
  )

  // Bank Tab
  const {
    // isLoading,
    // error,
    data: bank,
  } = useQuery(
    ['employeeBankData', params.id],
    async () => {
      const { data } = await getBankData(params.id)
      return data
    },
    {
      onError: (err) => {
        Bugsnag.notify(err)
      },
    }
  )
  const {
    // isLoading,
    // error,
    data: salCat,
  } = useQuery(
    'salaryCategories',
    async () => {
      const { data } = await salaryCategories()
      return data
    },
    {
      onError: (err) => {
        Bugsnag.notify(err)
      },
    }
  )
  const {
    // isLoading,
    // error,
    data: bankDropdown,
  } = useQuery(
    'bankCategories',
    async () => {
      const { data } = await banks()
      return data
    },
    {
      onError: (err) => {
        Bugsnag.notify(err)
      },
    }
  )

  // Tax Tab
  const {
    // isLoading,
    // error,
    data: tax,
  } = useQuery(
    ['employeeTaxData', params.id],
    async () => {
      const { data } = await getTaxData(params.id)
      return data
    },
    {
      onError: (err) => {
        Bugsnag.notify(err)
      },
    }
  )

  // company hrdf status
  const { data: hrdf_status } = useQuery(
    ['companyDetails'],
    async () => {
      const { data } = await getCompanyDetails()
      return data?.hrdf_status
    },
    {
      onError: (error) => {
        if (error.status !== 403) {
          Bugsnag.notify(error)
        }
      },
    }
  )

  useEffect(() => {
    if (!personal || !employment || !bank || !tax) return

    const to2dp = (v) => {
      if (v === null || v === undefined || v === '') return '0.00'
      const n = Number(String(v).replace(/,/g, ''))
      if (Number.isNaN(n)) return '0.00'
      return n.toFixed(2)
    }

    // derive Malaysian vs foreign for default worker type
    const isMalaysian =
      (personal?.data?.nationaliy || personal?.data?.nationality || 'Malaysia') ===
      'Malaysia'

    // derive worker type: use stored value if available, otherwise fallback to nationality
    const derivedWorkerType =
      bank?.data?.socso_worker_type ?? (isMalaysian ? 1 : 2)

    // pick the correct category list based on worker type
    const socsoCategoryList =
      derivedWorkerType === 2 ? SOCSOCategoryForeign : SOCSOCategoryLocal

    let initialSocsoCategory = null
    if (bank?.data?.socso_category) {
      initialSocsoCategory =
        socsoCategoryList.find((item) => item.id === bank.data.socso_category) ||
        null
    }

    // foreign worker → force EIS = 0
    const resolvedEisStatus =
      derivedWorkerType === 2 ? 0 : bank.data.eis_status

    setInitialValues({
      //basic details
      name: personal.data.name,
      gender: personal.data.gender,
      nationality: personal.data.nationaliy ? personal.data.nationaliy : 'Malaysia',
      disability_status: personal.data.disability_status,
      dob: personal?.data?.dob,
      id_type: personal.data.id_type,
      id_number: personal.data.id_number,

      //contact details
      phone_country_id: personal.data.phone_country_id,
      phone_no: personal.data.phone_no,
      personal_email: personal.data.personal_email,
      address: personal.data.address,
      postcode: personal.data.postcode,
      state: personal.data.state,
      country: personal.data.country ? personal.data.country : 'Malaysia',
      emergency_contact_name: personal.data.emergency_contact_name,
      emergency_contact_relationship: personal.data.emergency_contact_relationship,
      emergency_contact_phone_country_id: personal.data.emergency_contact_phone_country_id,
      emergency_contact_phone_no: personal.data.emergency_contact_phone_no,

      //employment details
      employment_type: employment.data.employment_type_id,
      hiring_date: employment.data.hiring_date,
      commencement_date: employment.data.commencement_date,

      //job information
      internal_employee_id: employment.data.internal_employee_id,
      designation: employment.data.designation,
      job_description: employment.data.job_description,
      department_id: employment.data.department_id,
      reporting_to_id: employment.data.reporting_to_id
        ? {
          id: employment.data.reporting_to_id,
          name: employment.data.reporting_to_name,
          designation: employment.data.reporting_to_designation,
          profile_image_url: employment.data.reporting_to_image,
        }
        : null,

      //salary details
      salary_category: bank.data.salary_category,
      basic_salary: bank.data.basic_salary,
      bank_id: bank.data.bank_id,
      bank_acc_number: bank.data.bank_acc_number,

      //statutory details
      epf_status: bank.data.epf_status,
      epf_category: bank?.data?.epf_category
        ? EPFCategory.find((item) => item.id === bank?.data?.epf_category)
        : { id: 0, category: 'No Category' },
      epf_acc_number: bank.data.epf_acc_number,
      epf_employee_contribution_rate: bank.data.epf_employee_contribution_rate,
      employee_custom_contribution_rate: bank.data.employee_custom_contribution_rate,
      epf_employer_contribution_rate: bank.data.epf_employer_contribution_rate,
      employer_custom_contribution_rate: bank.data.employer_custom_contribution_rate,

      socso_worker_type: derivedWorkerType,
      socso_status: bank.data.socso_status,
      socso_category: bank.data.socso_status === 1 ? initialSocsoCategory : null,
      socso_acc_number: bank.data.socso_acc_number,
      eis_status: resolvedEisStatus,
      hrdf_status: bank.data.employee_hrdf_status,
      zakat_status: bank.data.zakat_status,
      zakat_acc_number: bank.data.zakat_acc_number,
      zakat_amount: bank.data.zakat_amount,

      //tax details
      resident_status: tax.data.resident_status,
      income_tax_number: tax.data.income_tax_number,
      previous_mtd: tax.data.previous_mtd,
      epf_contribution: tax.data.epf_contribution,
      gross_remuneration: tax.data.gross_remuneration,
      previous_mtd: to2dp(tax.data.previous_mtd),
      epf_contribution: to2dp(tax.data.epf_contribution),
      gross_remuneration: to2dp(tax.data.gross_remuneration),

      //household details
      marriage_status: tax.data.marriage_status,
      spouse_disability_status: tax.data.spouse_disability_status,
      children: tax.data.children
        ? tax.data.children.map((item) => ({
          id: item.id,
          dob: new Date(item.dob),
          education_status: item.education_status,
          disability_status: item.disability_status,
        }))
        : [],
    })
  }, [personal, employment, bank, tax]) // eslint-disable-line react-hooks/exhaustive-deps
  // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseModal = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    // setWarningOpen(false)
    navigate(-1)
  }

  const mutation = useMutation((value) => postAllData(params.id, value), {
    onSuccess: (response) => {
      queryClient.invalidateQueries(['detailedEmployeeData', params.id])
      queryClient.invalidateQueries(['employeePersonalData', params.id])
      queryClient.invalidateQueries(['employeeEmploymentData', params.id])
      queryClient.invalidateQueries(['employeeBankData', params.id])
      queryClient.invalidateQueries(['employeeTaxData', params.id])
      setPageState('SUCCESSFUL_EDIT')
    },
    onError: (error) => {
      setPageState('')
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleConfirm = async () => {
    mutation.mutate(formikData)
  }
  // destructure values
  const handleSubmitAll = async (values, actions) => {
    let newValues = {
      ...values,
      children: values.children.map((item) => {
        const { dob, ...others } = item
        return {
          ...others,
          dob: format(dob, 'MM/dd/yyyy'),
        }
      }),
      dob: _convertAPIDate(values.dob),
      epf_category: values.epf_category.id,
      socso_category: values.socso_category ? values.socso_category?.id : null,
      reporting_to_id: values.reporting_to_id.id,
    }
    let isMatched = _ICBirthdateValidation(newValues)
    if (newValues.id_type === 'nric') {
      if (isMatched) {
        setFormikData(newValues)
        setPageState('CONFIRM_EDIT')
      } else {
        let errors = {
          dob: [dobIDValidateText],
          id_number: [dobIDValidateText],
        }
        let newError = {
          data: {
            errors,
          },
        }
        handleValidationErrors(newError, formikRef)
      }
    } else {
      setFormikData(newValues)
      setPageState('CONFIRM_EDIT')
    }
  }

  if (pageState === 'CONFIRM_EDIT') {
    return (
      <AdxInfoModal
        variant={'standard'}
        open={true}
        muiIcon="check_circle"
        iconColor="alternate.main"
        title="Would you like to save the changes made?"
        subtitle={``}
        footerButtons
        firstBtnLabel={'Confirm'}
        firstBtnHandle={handleConfirm}
        secondBtnLabel={'Back'}
        secondBtnHandle={() => setPageState('')}
      ></AdxInfoModal>
    )
  }

  if (pageState === 'SUCCESSFUL_EDIT') {
    return (
      <AdxInfoModal
        open={true}
        muiIcon="check_circle"
        iconColor="alternate.main"
        title="Employee Details has been successfully saved"
        subtitle={``}
        footerButtons
        firstBtnLabel={'Return to Manage Employee Page'}
        firstBtnHandle={() =>
          navigate(`/app/management-centre/staff-management/employee-file/${params.id}`)
        }
      ></AdxInfoModal>
    )
  }

  const cancelEditForm = (resetForm) => {
    resetForm()
    navigate(-1)
  }

  const handleCheckRef = async (handleSubmit) => {
    await handleSubmit()
    const { errors = {} } = formikRef.current
    const arrayError = Object.keys(errors)
    if (arrayError.length) {
      const formModal = document.getElementById('form-modal')
      // Get Key Name From Tabs in Array of String
      let defaultEmpty = []
      for (let i = 0; i < sortedObjectAll.length; i++) {
        let filteredArray = arrayError.filter((filtered) => sortedObjectAll[i].includes(filtered))
        const fieldName = filteredArray[0]
        const escapedFieldName = fieldName && (window.CSS?.escape ? window.CSS.escape(fieldName) : fieldName)
        let errorName = fieldName ? formModal?.querySelector(`[name="${escapedFieldName}"]`) : null
        if (errorName) {
          errorName.scrollIntoView({ behavior: 'smooth', block: 'center' })
          defaultEmpty.push(i)
        } else if (filteredArray.length !== 0) {
          defaultEmpty.push(i)
        }
      }
      setErrorTabs(defaultEmpty)
    }
  }

  const validationSchema = Yup.object().shape({
    // Basic Details
    name: Yup.string()
      .nullable()
      .required('Employee Name is required')
      .matches(/^[a-zA-Z-@ /.']+$/, "Name must be valid eg. Alphabet with @, ', -, . and /"),
    gender: Yup.string().nullable().required('Gender is required'),
    nationality: Yup.string().nullable().required('Nationality is required'),
    disability_status: Yup.string().nullable().required('Disability Status is required'),
    dob: Yup.string().nullable().required('Date of Birth is required'),
    id_type: Yup.string().nullable().required('Identification Type is required'),
    id_number: Yup.string()
      .nullable()
      .when('id_type', {
        is: 'nric',
        then: Yup.string()
          .nullable()
          .required('IC Number is required')
          .matches(/^[0-9]{12}?$/, 'IC Number must be valid, eg. 880208104178'),
        otherwise: Yup.string()
          .nullable()
          .required('Passport Number is required')
          .matches(/^[A-Z\d]+$/i, 'Passport Number must be alphanumeric, eg. A32127654'),
      }),
    // Contact Details
    phone_no: Yup.string()
      .nullable()
      .required('Phone Number is required')
      .matches(/^[0-9\b]+$/, 'Phone Numbers can only contain numbers'),
    phone_country_id: Yup.string().nullable().required('Phone Code is required'),
    personal_email: Yup.string()
      .email('Invalid Email Address')
      .nullable()
      .required('Personal Email is required'),
    address: Yup.string().nullable().required('Home Address is required'),
    postcode: Yup.string()
      .nullable()
      .when('country', {
        is: 'Malaysia',
        then: Yup.string()
          .nullable()
          .matches(/^[0-9]{5}?$/, 'Postal Code must be in number, eg. 56000'),
        otherwise: Yup.string()
          .nullable()
          .matches(/^[a-z\d\s]+$/i, 'Postal Code must be alphanumeric, eg. S10 2GA'),
      })
      .required('Postal Code is required'),
    state: Yup.string().nullable().required('State is required'),
    country: Yup.string().nullable().required('Country is required'),
    emergency_contact_name: Yup.string()
      .nullable()
      .required('Emergency Contact Name is required')
      .matches(/^[a-zA-Z-@ /.']+$/, "Name must be valid eg. Alphabet with @, ', -, . and /"),
    emergency_contact_relationship: Yup.string().nullable().required('Relationship is required'),
    emergency_contact_phone_no: Yup.string()
      .nullable()
      .required('Phone Number is required')
      .matches(/^[0-9\b]+$/, 'Phone Numbers can only contain numbers'),
    emergency_contact_phone_country_id: Yup.string().nullable().required('Phone Code is required'),
    // Basic Employment Details
    employment_type: Yup.number().nullable().required('Employment Type is required'),
    hiring_date: Yup.date().nullable().required('Hiring Date is required'),
    commencement_date: Yup.date()
      .nullable()
      .min(Yup.ref('hiring_date'), "Commencement Date can't be before Hiring Date")
      .required('Commencement Date is required'),
    // Job Information
    internal_employee_id: Yup.string()
      .nullable()
      .required('Employee ID is required')
      .matches(/^[a-zA-Z0-9]+$/, 'Employee ID must be alphanumeric'),
    designation: Yup.string()
      .nullable()
      .required('Job Designation is required')
      .matches(/^[A-Z\d\-()\s]+$/i, 'Job Designation must be valid'),
    job_description: Yup.string().nullable().required('Job Description is required'),
    department_id: Yup.number().nullable().required('Department is required'),
    reporting_to_id: !employment?.data?.head_organisation
      ? Yup.object().nullable().required('Reporting Manager is required')
      : Yup.object().nullable(),
    // Salary Details
    salary_category: Yup.string().nullable().required('Salary Category is required'),
    basic_salary: Yup.string()
      .nullable()
      .required('Basic Salary is required')
      .matches(/^[0-9]{1,11}(?:\.[0-9]{1,2})?$/, 'Salary must be valid, eg. RM XXXX.XX'),
    bank_id: Yup.string().nullable().required('Bank Name is required'),
    bank_acc_number: Yup.string()
      .nullable()
      .required('Bank Account Number is required')
      .matches(/^[0-9\b]+$/, 'Bank Account Number can only contain numbers'),
    // Statutory Body Details
    epf_status: Yup.number().nullable().required('EPF Contribution Status is required'),
    epf_category: Yup.object()
      .nullable()
      .when('epf_status', {
        is: 1,
        then: Yup.object().nullable().required('EPF Category is required'),
        otherwise: Yup.object().nullable(),
      }),
    epf_acc_number: Yup.string()
      .nullable()
      .when('epf_status', {
        is: 1,
        then: Yup.string()
          .nullable()
          .required('EPF Account Number is required')
          .matches(/^[0-9\b]+$/, 'EPF Account Number can only contain numbers'),
        otherwise: Yup.string().nullable(),
      }),
    epf_employee_contribution_rate: Yup.number()
      .nullable()
      .when('epf_status', {
        is: 1,
        then: Yup.number().nullable().required('Employee EPF Rate is required'),
        otherwise: Yup.number().nullable(),
      }),
    employee_custom_contribution_rate: Yup.string()
      .nullable()
      .when('epf_employee_contribution_rate', {
        is: 2,
        then: Yup.string()
          .nullable()
          .required('Custom Rate is required')
          .matches(/^(0?[0-9]|1[0123456789])$/, 'Custom Rate must not be more than 19%'),
        otherwise: Yup.string().nullable(),
      }),

    epf_employer_contribution_rate: Yup.number()
      .nullable()
      .when('epf_status', {
        is: 1,
        then: Yup.number().nullable().required('Employer EPF Rate is required'),
        otherwise: Yup.number().nullable(),
      }),
    employer_custom_contribution_rate: Yup.string()
      .nullable()
      .when('epf_employer_contribution_rate', {
        is: 1,
        then: Yup.string()
          .nullable()
          .required('Custom Rate is required')
          .matches(/^(0?[0-9]|1[0123456789])$/, 'Custom Rate must not be more than 19%'),
        otherwise: Yup.string().nullable(),
      }),
    socso_worker_type: Yup.number().nullable().required('SOCSO Worker Type is required'),
    socso_status: Yup.number().nullable().required('SOCSO Contribution Status is required'),

    socso_category: Yup.object()
      .nullable()
      .when('socso_status', {
        is: 1,
        then: Yup.object().nullable().required('SOCSO Category is required'),
        otherwise: Yup.object().nullable(),
      }),
    socso_acc_number: Yup.string()
      .nullable()
      .when('socso_status', {
        is: 1,
        then: Yup.string()
          .nullable()
          .required('SOCSO Account Number is required')
          .matches(/^[a-zA-Z0-9-]+$/, 'SOCSO Account Number can only contain alphanumeric'),
        otherwise: Yup.string().nullable(),
      }),
    eis_status: Yup.number().nullable().required('EIS Contribution Status is required'),
    hrdf_status: Yup.number().nullable().required('Employee HRDF status is required'),
    zakat_status: Yup.number().nullable().required('Zakat Contribution Status is required'),
    zakat_acc_number: Yup.string()
      .nullable()
      .when('zakat_status', {
        is: 1,
        then: Yup.string()
          .nullable()
          .required('Zakat Account Number is required')
          .matches(/^[0-9\b]+$/, 'Zakat Account Number can only contain numbers'),
        otherwise: Yup.string().nullable(),
      }),
    zakat_amount: Yup.string()
      .nullable()
      .when('zakat_status', {
        is: 1,
        then: Yup.string()
          .nullable()
          .required('Zakat Amount is required')
          .matches(/^[0-9]{1,11}(?:\.[0-9]{1,3})?$/, 'Zakat Amount must be valid'),
        otherwise: Yup.string().nullable(),
      }),
    // Tax Details
    resident_status: Yup.number().nullable().required('Please select a status'),
    income_tax_number: Yup.string()
      .nullable()
      .required('Income Tax Number is required')
      .matches(/^[a-z\d\s]+$/i, 'Income Tax Number must be alphanumeric'),
    previous_mtd: Yup.string()
      .nullable()
      .required('Amount is required')
      .matches(
        /^[0-9]{1,11}(?:\.[0-9]{1,2})?$/,
        'Previous Months MTD must be valid, eg. RM XXX.XX'
      ),
    epf_contribution: Yup.string()
      .nullable()
      .required('Amount is required')
      .matches(
        /^[0-9]{1,11}(?:\.[0-9]{1,2})?$/,
        'Accumulated Months EPF Contribution must be valid, eg. RM XXX.XX'
      ),
    gross_remuneration: Yup.string()
      .nullable()
      .required('Amount is required')
      .matches(
        /^[0-9]{1,11}(?:\.[0-9]{1,2})?$/,
        'Accumulated Months Gross Remuneration must be valid, eg. RM XXX.XX'
      ),
    // Household Details
    marriage_status: Yup.number().nullable().required('Marriage Status is required'),
    spouse_disability_status: Yup.number().nullable().required('Disability Status is required'),
    children: Yup.array().of(
      Yup.object().shape({
        dob: Yup.string().nullable().required('Date of birth is required'),
        education_status: Yup.number().nullable().required('Education Status is required'),
        disability_status: Yup.number().nullable().required('Disability Status is required'),
      })
    ),
  })

  if (pageState === '')
    return (
      <>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmitAll}
          enableReinitialize
          validationSchema={validationSchema}
          innerRef={formikRef}
        >
          {(formikBag) => (
            <Form>
              <AdxFormModal
                title="Edit all information"
                id={'form-modal'}
                open={true}
                handleClose={handleCloseModal}
                disableEscapeKeyDown
                disableAutoFocus
                childPadding={false}
                enableFooter
                actionText="Save"
                handleAction={() => handleCheckRef(formikBag.handleSubmit)}
                responsiveScroll
                sxModal={{ width: { xs: 'calc(100% - 24px)', md: 700 }, minWidth: 'auto' }}
              >
                <AdxTabsBadge
                  scrollButtons={false}
                  initialValue={0}
                  sxTabs={{ minHeight: 0 }}
                  sxPanel={{ overflowY: 'visible' }}
                >


                  <AdxTabsPanel label="1. Personal">
                    <EditPersonalTab formikBag={formikBag} countriesList={countriesList} />
                  </AdxTabsPanel>
                  <AdxTabsPanel label="2. Employment">
                    <EditEmploymentTab
                      formikBag={formikBag}
                      departmentList={departmentList}
                      employmentData={employment}
                    />
                  </AdxTabsPanel>
                  <AdxTabsPanel label={`3. Bank & Statutory`}>
                    <EditBankTab
                      formikBag={formikBag}
                      salCat={salCat}
                      bankDropdown={bankDropdown}
                      EPFCategory={EPFCategory}
                      SOCSOCategoryLocal={SOCSOCategoryLocal}
                      SOCSOCategoryForeign={SOCSOCategoryForeign}
                      SOCSOWorkerTypeOptions={SOCSOWorkerTypeOptions}
                      companyHrdfStatus={hrdf_status}
                    />
                  </AdxTabsPanel>

                  <AdxTabsPanel label="4. Tax">
                    <EditTaxTab formikBag={formikBag} />
                  </AdxTabsPanel>
                </AdxTabsBadge>
                <Box sx={{ p: 2 }}>
                  {Object.keys(formikBag.errors).length !== 0 &&
                    errorTabs.map((itemIndexTab) => (
                      <AdxErrorMessage>
                        Please fix the following errors in {tabNames[itemIndexTab]} tab
                      </AdxErrorMessage>
                    ))}
                  {formikRef?.current?.errors?.dob === dobIDValidateText &&
                    formikRef?.current?.errors?.id_number === dobIDValidateText && (
                      <AdxErrorMessage>
                        {/* Please fix the following errors in Personal tab */}
                        {dobIDValidateText}
                      </AdxErrorMessage>
                    )}
                </Box>

                {/* Keeping this until no issues are reported */}
                {/* <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: 1,
                  }}
                >
                  <AdxButton
                    onClick={() => cancelEditForm(formikBag.resetForm)}
                    sx={{ width: 0.2 }}
                  >
                    Cancel
                  </AdxButton>
                  {formikBag.isSubmitting ? (
                    <AdxButton isLoading sx={{ width: 0.2 }}>
                      Save
                    </AdxButton>
                  ) : (
                    <AdxButton
                      onClick={() => handleCheckRef(formikBag.handleSubmit)}
                      sx={{ width: 0.2 }}
                    >
                      Save
                    </AdxButton>
                  )}
                </Box> */}
              </AdxFormModal>
            </Form>
          )}
        </Formik>
      </>
    )
}

export default EditAllStaff
